import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { ESRJ_MODULE } from '../../../self-reflection-journeys/ethical-self-reflection/esrj_module.enum';
import { IEsrjCompletedStep } from '../../interfaces/IEsrjCompeltedStep';
import { JourneyConditionalOutputRecord } from '../../interfaces/IJourneyConditionalOutput';
import { JourneyMultipleChoiceItem } from '../../interfaces/IJourneyMultipleChoice';
import { JourneyRatingData } from '../../interfaces/IJourneyRating';
import { IEthicalContrast, IEthicalGoal, IEthicalInfluence, IEthicalIntention, IUserOwnDilemmaInput } from '../../interfaces/IUserInput';
import {
  ResetESRJStatus,
  ResetESRJSteps,
  UpdateESRJAspectsOfEthics,
  UpdateESRJCompletedAspectsOfEthics,
  UpdateESRJCompletedSelectedDilemmas,
  UpdateESRJCompletedSteps,
  UpdateESRJEthicalBehaviorResponses,
  UpdateESRJEthicalClimateResponses,
  UpdateESRJEthicalClimateTextInputs,
  UpdateESRJEthicalGoalObjective,
  UpdateESRJEthicalImplementationIntention,
  UpdateESRJEthicalLeadershipResponses,
  UpdateESRJEthicalLeadershipTextInputs,
  UpdateESRJEthicalMentalContrast,
  UpdateESRJEthicalMindfulResponses,
  UpdateESRJEthicalOwnDilemma,
  UpdateESRJEthicalSelectedDilemma,
  UpdateESRJEthicalSelectedDilemmaTextInput,
} from './ethicalSelfReflection.actions';

const DEFAULT_STATE: EthicalSelfReflectionStateModel = {
  status: null,
  completedSteps: [],
  aspectsOfEthics: [],
  completedAspectsOfEthics: new Set<string>(),
  ethicalBehaviorResponses: null,
  ethicalMindfulResponses: null,
  ethicalSelectedDilemma: [],
  completedSelectedDilemmas: new Set<string>(),
  ethicalLeadershipResponses: null,
  ethicalLeadershipTextInputs: null,
  ethicalClimateResponses: null,
  ethicalClimateTextInputs: null,
  ethicalOwnDilemma: null,
  ethicalSelectedDilemmaTextInput: null,
  ethicalImplementationIntention: null,
  ethicalMentalContrast: null,
  ethicalGoalObjective: null,
};

export class EthicalSelfReflectionStateModel {
  completedSteps: IEsrjCompletedStep[];
  status: 'finished' | 'started' | null; //if last step is completed => finished
  aspectsOfEthics: JourneyMultipleChoiceItem[]; // from step 1_2_3
  completedAspectsOfEthics: Set<string>;
  ethicalBehaviorResponses: JourneyRatingData; // from step 1_2_4
  ethicalMindfulResponses: JourneyRatingData; // from step baseline_2
  ethicalSelectedDilemma: JourneyMultipleChoiceItem[];
  completedSelectedDilemmas: Set<string>;
  ethicalLeadershipResponses: JourneyRatingData;
  ethicalClimateResponses: JourneyRatingData;
  ethicalLeadershipTextInputs: IEthicalInfluence;
  ethicalClimateTextInputs: IEthicalInfluence;
  ethicalOwnDilemma: IUserOwnDilemmaInput;
  ethicalSelectedDilemmaTextInput: IUserOwnDilemmaInput;
  ethicalImplementationIntention: IEthicalIntention;
  ethicalMentalContrast: IEthicalContrast;
  ethicalGoalObjective: IEthicalGoal;
}

@State<EthicalSelfReflectionStateModel>({
  name: 'ethicalSelfReflection',
  defaults: DEFAULT_STATE,
})
@Injectable()
export class EthicalSelfReflectionState {
  @Selector()
  static ethicalSelfReflectionJourneyCompletedSteps(state: EthicalSelfReflectionStateModel): IEsrjCompletedStep[] {
    return state.completedSteps;
  }

  @Selector()
  static ethicalSelfReflectionJourneyAllAspectsOfEthics(state: EthicalSelfReflectionStateModel): JourneyMultipleChoiceItem[] {
    return state.aspectsOfEthics;
  }

  @Selector()
  static ethicalSelfReflectionJourneyCompletedAspectsOfEthics(state: EthicalSelfReflectionStateModel): Set<string> {
    return state.completedAspectsOfEthics;
  }

  @Selector()
  static ethicalSelfReflectionJourneyAspectsOfEthics(state: EthicalSelfReflectionStateModel): JourneyMultipleChoiceItem[] {
    return state.aspectsOfEthics.filter((aspect: JourneyMultipleChoiceItem) => aspect.selected);
  }

  @Selector()
  static ethicalSelfReflectionJourneyAspectsOfEthicsIds(state: EthicalSelfReflectionStateModel): string {
    return state.aspectsOfEthics.find((aspect: JourneyMultipleChoiceItem) => aspect.selected)?.id ?? null;
  }

  @Selector()
  static ethicalSelfReflectionJourneyAspectsOfEthicsName(state: EthicalSelfReflectionStateModel): string {
    return state.aspectsOfEthics.find((aspect: JourneyMultipleChoiceItem) => aspect.selected)?.title ?? '';
  }

  @Selector()
  static ethicalSelfReflectionJourneyEthicalSelectedDilemma(state: EthicalSelfReflectionStateModel): JourneyMultipleChoiceItem[] {
    return state.ethicalSelectedDilemma;
  }

  @Selector()
  static ethicalSelfReflectionJourneyCompletedSelectedDilemmas(state: EthicalSelfReflectionStateModel): Set<string> {
    return state.completedSelectedDilemmas;
  }

  @Selector()
  static ethicalSelfReflectionJourneyEthicalSelectedDilemmaOption(state: EthicalSelfReflectionStateModel): JourneyMultipleChoiceItem {
    return state.ethicalSelectedDilemma.find((option: JourneyMultipleChoiceItem) => option.selected);
  }

  @Selector()
  static ethicalSelfReflectionJourneyEthicalBehaviorResponses(state: EthicalSelfReflectionStateModel): JourneyRatingData {
    return state.ethicalBehaviorResponses;
  }

  @Selector()
  static ethicalSelfReflectionJourneyEthicalMindfulResponses(state: EthicalSelfReflectionStateModel): JourneyRatingData {
    return state.ethicalMindfulResponses;
  }

  @Selector()
  static ethicalSelfReflectionJourneyEthicalBehaviorResponsesAsConditionalRecords(state: EthicalSelfReflectionStateModel): JourneyConditionalOutputRecord[] {
    const defaultValence: 'positive' | 'negative' = 'positive';
    return state.ethicalBehaviorResponses.questions.map(({ label, answer = '', valence = defaultValence, group }) => ({
      question: label,
      answer,
      valence,
      topic: state.aspectsOfEthics.find(({ id }) => id === group)?.title || '',
    }));
  }

  @Selector()
  static ethicalSelfReflectionJourneyEthicalLeadershipResponses(state: EthicalSelfReflectionStateModel): JourneyRatingData {
    return state.ethicalLeadershipResponses;
  }

  @Selector()
  static ethicalSelfReflectionJourneyEthicalLeadershipTextInputs(state: EthicalSelfReflectionStateModel): IEthicalInfluence {
    return state.ethicalLeadershipTextInputs;
  }

  @Selector()
  static ethicalSelfReflectionJourneyEthicalLeadershipResponsesAsConditionalRecords(state: EthicalSelfReflectionStateModel): JourneyConditionalOutputRecord[] {
    const defaultValence: 'positive' | 'negative' = 'positive';
    const defaultInverse = false;

    return state.ethicalLeadershipResponses
      ? state.ethicalLeadershipResponses.questions.map(({ label, answer = '', valence = defaultValence, isInverse = defaultInverse }) => ({
          question: label,
          answer,
          valence,
          topic: '',
          isInverse,
        }))
      : [];
  }

  @Selector()
  static ethicalSelfReflectionJourneyEthicalClimateResponses(state: EthicalSelfReflectionStateModel): JourneyRatingData {
    return state.ethicalClimateResponses;
  }

  @Selector()
  static ethicalSelfReflectionJourneyEthicalClimateTextInputs(state: EthicalSelfReflectionStateModel): IEthicalInfluence {
    return state.ethicalClimateTextInputs;
  }

  @Selector()
  static ethicalSelfReflectionJourneyEthicalClimateResponsesAsConditionalRecords(state: EthicalSelfReflectionStateModel): JourneyConditionalOutputRecord[] {
    const defaultValence: 'positive' | 'negative' = 'positive';
    const defaultInverse = false;
    return state.ethicalClimateResponses
      ? state.ethicalClimateResponses.questions.map(({ label, group, answer = '', valence = defaultValence, isInverse = defaultInverse }) => ({
          question: label,
          answer,
          valence,
          topic: group,
          isInverse,
        }))
      : [];
  }

  @Selector()
  static ethicalSelfReflectionJourneyEthicalOwnDilemma(state: EthicalSelfReflectionStateModel): IUserOwnDilemmaInput {
    return state.ethicalOwnDilemma;
  }

  @Selector()
  static ethicalSelfReflectionJourneyEthicalSelectedDilemmaTextInput(state: EthicalSelfReflectionStateModel): IUserOwnDilemmaInput {
    return state.ethicalSelectedDilemmaTextInput;
  }

  @Selector()
  static ethicalSelfReflectionJourneyImplementationIntention(state: EthicalSelfReflectionStateModel): IEthicalIntention {
    return state.ethicalImplementationIntention;
  }

  @Selector()
  static ethicalSelfReflectionJourneyMentalContrast(state: EthicalSelfReflectionStateModel): IEthicalContrast {
    return state.ethicalMentalContrast;
  }

  @Selector()
  static ethicalSelfReflectionGoalObjective(state: EthicalSelfReflectionStateModel): IEthicalGoal {
    return state.ethicalGoalObjective;
  }

  @Action(UpdateESRJCompletedSteps)
  updateESRJCompletedSteps(ctx: StateContext<EthicalSelfReflectionStateModel>, action: UpdateESRJCompletedSteps): void {
    const state = ctx.getState();
    const stepIndex = state.completedSteps.findIndex(step => step.stepId === action.newStep.stepId);
    let updatedCompletedSteps = state.completedSteps;
    if (stepIndex > -1) {
      updatedCompletedSteps[stepIndex] = action.newStep;
    } else {
      updatedCompletedSteps = [...state.completedSteps, action.newStep];
    }
    ctx.patchState({
      completedSteps: updatedCompletedSteps,
    });
  }

  @Action(UpdateESRJAspectsOfEthics)
  updateESRJAspectsOfEthics(ctx: StateContext<EthicalSelfReflectionStateModel>, action: UpdateESRJAspectsOfEthics): void {
    ctx.patchState({
      aspectsOfEthics: action.aspectsOfEthics,
    });
  }

  @Action(UpdateESRJCompletedAspectsOfEthics)
  updateESRJComepletedAspectsOfEthics(ctx: StateContext<EthicalSelfReflectionStateModel>, action: UpdateESRJCompletedAspectsOfEthics): void {
    ctx.patchState({
      completedAspectsOfEthics: action.completedAspectsOfEthics,
    });
  }

  @Action(UpdateESRJEthicalSelectedDilemma)
  updateESRJEthicalSelectedDilemma(ctx: StateContext<EthicalSelfReflectionStateModel>, action: UpdateESRJEthicalSelectedDilemma): void {
    ctx.patchState({
      ethicalSelectedDilemma: action.ethicalSelectedDilemma,
    });
  }

  @Action(UpdateESRJCompletedSelectedDilemmas)
  updateESRJEthicalCompletedSelectedDilemmas(ctx: StateContext<EthicalSelfReflectionStateModel>, action: UpdateESRJCompletedSelectedDilemmas): void {
    ctx.patchState({
      completedSelectedDilemmas: action.completedSelectedDilemmas,
    });
  }

  @Action(UpdateESRJEthicalBehaviorResponses)
  updateESRJEthicalBehaviorResponses(ctx: StateContext<EthicalSelfReflectionStateModel>, action: UpdateESRJEthicalBehaviorResponses): void {
    ctx.patchState({
      ethicalBehaviorResponses: action.ethicalBehaviorResponses,
    });
  }

  @Action(UpdateESRJEthicalMindfulResponses)
  updateESRJEthicalMindfulResponses(ctx: StateContext<EthicalSelfReflectionStateModel>, action: UpdateESRJEthicalMindfulResponses): void {
    ctx.patchState({
      ethicalMindfulResponses: action.ethicalMindfulResponses,
    });
  }

  @Action(UpdateESRJEthicalLeadershipResponses)
  updateESRJEthicalLeadershipResponses(ctx: StateContext<EthicalSelfReflectionStateModel>, action: UpdateESRJEthicalLeadershipResponses): void {
    ctx.patchState({
      ethicalLeadershipResponses: action.ethicalLeadershipResponses,
    });
  }

  @Action(UpdateESRJEthicalClimateResponses)
  updateESRJEthicalClimateResponses(ctx: StateContext<EthicalSelfReflectionStateModel>, action: UpdateESRJEthicalClimateResponses): void {
    ctx.patchState({
      ethicalClimateResponses: action.ethicalClimateResponses,
    });
  }

  @Action(UpdateESRJEthicalLeadershipTextInputs)
  updateESRJEthicalLeadershipTextInputs(ctx: StateContext<EthicalSelfReflectionStateModel>, action: UpdateESRJEthicalLeadershipTextInputs): void {
    ctx.patchState({
      ethicalLeadershipTextInputs: action.ethicalLeadershipTextInputs,
    });
  }

  @Action(UpdateESRJEthicalClimateTextInputs)
  updateESRJEthicalClimateTextInputs(ctx: StateContext<EthicalSelfReflectionStateModel>, action: UpdateESRJEthicalClimateTextInputs): void {
    ctx.patchState({
      ethicalClimateTextInputs: action.ethicalClimateTextInputs,
    });
  }

  @Action(UpdateESRJEthicalOwnDilemma)
  updateESRJEthicalOwnDilemma(ctx: StateContext<EthicalSelfReflectionStateModel>, action: UpdateESRJEthicalOwnDilemma): void {
    ctx.patchState({
      ethicalOwnDilemma: action.ethicalOwnDilemma,
    });
  }

  @Action(UpdateESRJEthicalSelectedDilemmaTextInput)
  updateESRJEthicalSelectedDilemmaTextInput(ctx: StateContext<EthicalSelfReflectionStateModel>, action: UpdateESRJEthicalSelectedDilemmaTextInput): void {
    ctx.patchState({
      ethicalSelectedDilemmaTextInput: action.ethicalSelectedDilemmaTextInput,
    });
  }

  @Action(UpdateESRJEthicalImplementationIntention)
  updateEthicalIfThenStatements(ctx: StateContext<EthicalSelfReflectionStateModel>, action: UpdateESRJEthicalImplementationIntention): void {
    ctx.patchState({ ethicalImplementationIntention: action.ethicalImplementationIntention });
  }

  @Action(UpdateESRJEthicalMentalContrast)
  updateEthicalMentalContrast(ctx: StateContext<EthicalSelfReflectionStateModel>, action: UpdateESRJEthicalMentalContrast): void {
    ctx.patchState({ ethicalMentalContrast: action.ethicalMentalContrast });
  }

  @Action(UpdateESRJEthicalGoalObjective)
  updateEthicalGoalObjective(ctx: StateContext<EthicalSelfReflectionStateModel>, action: UpdateESRJEthicalGoalObjective): void {
    ctx.patchState({ ethicalGoalObjective: action.ethicalGoalObjective });
  }

  @Action(ResetESRJStatus)
  resetESRJStatus(ctx: StateContext<EthicalSelfReflectionStateModel>): void {
    ctx.setState(DEFAULT_STATE);
  }

  @Action(ResetESRJSteps)
  resetESRJSteps(ctx: StateContext<EthicalSelfReflectionStateModel>, resetSteps: { newStepsArray: IEsrjCompletedStep[] }): void {
    const currentState = ctx.getState();
    const newStepsArray = resetSteps.newStepsArray;
    ctx.patchState({
      ...currentState,
      completedSteps: newStepsArray,
      aspectsOfEthics: this._isStepStillCompleted(ESRJ_MODULE.MODULE1_CHAPTER2_STEP3, newStepsArray) ? currentState.aspectsOfEthics : [],
      completedAspectsOfEthics: this._isStepStillCompleted(ESRJ_MODULE.MODULE1_CHAPTER2_STEP3, newStepsArray)
        ? currentState.completedAspectsOfEthics
        : new Set<string>(),
      ethicalBehaviorResponses: this._isStepStillCompleted(ESRJ_MODULE.MODULE1_CHAPTER2_STEP4, newStepsArray) ? currentState.ethicalBehaviorResponses : null,
      ethicalMindfulResponses: this._isStepStillCompleted(ESRJ_MODULE.BASELINE_STEP2, newStepsArray) ? currentState.ethicalMindfulResponses : null,
      ethicalSelectedDilemma: this._isStepStillCompleted(ESRJ_MODULE.MODULE2_STEP2_B_2, newStepsArray) ? currentState.ethicalSelectedDilemma : [],
      completedSelectedDilemmas: this._isStepStillCompleted(ESRJ_MODULE.MODULE2_STEP2_B_2, newStepsArray)
        ? currentState.completedSelectedDilemmas
        : new Set<string>(),
      ethicalLeadershipResponses: this._isStepStillCompleted(ESRJ_MODULE.MODULE4_STEP2_A_1, newStepsArray) ? currentState.ethicalLeadershipResponses : null,
      ethicalClimateResponses: this._isStepStillCompleted(ESRJ_MODULE.MODULE4_STEP2_B_1, newStepsArray) ? currentState.ethicalClimateResponses : null,
      ethicalLeadershipTextInputs: this._isStepStillCompleted(ESRJ_MODULE.MODULE4_STEP2_A_2, newStepsArray) ? currentState.ethicalLeadershipTextInputs : null,
      ethicalClimateTextInputs: this._isStepStillCompleted(ESRJ_MODULE.MODULE4_STEP2_B_2, newStepsArray) ? currentState.ethicalClimateTextInputs : null,
      ethicalOwnDilemma: this._isStepStillCompleted(ESRJ_MODULE.MODULE2_STEP2_A_2, newStepsArray) ? currentState.ethicalOwnDilemma : null,
      ethicalSelectedDilemmaTextInput: this._isStepStillCompleted(ESRJ_MODULE.MODULE2_STEP2_B_3, newStepsArray)
        ? currentState.ethicalSelectedDilemmaTextInput
        : null,
      ethicalImplementationIntention: this._isStepStillCompleted(ESRJ_MODULE.MODULE3_STEP2_A_1, newStepsArray)
        ? currentState.ethicalImplementationIntention
        : null,
      ethicalMentalContrast: this._isStepStillCompleted(ESRJ_MODULE.MODULE3_STEP2_B_1, newStepsArray) ? currentState.ethicalMentalContrast : null,
    });
  }

  private _isStepStillCompleted(stepId: string, newCompletedSteps: IEsrjCompletedStep[]): boolean {
    return newCompletedSteps.some(step => step.stepId === stepId);
  }
}
